import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon, Warning } from "@mui/icons-material";
import axios from "axios";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode, hostname } from "../utils";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface ServiceStatus {
  name: string;
  key: string;
  description: string;
  status: "operational" | "degraded" | "outage";
  uptimePercentage: number;
  lastChecked: string | null;
  uptimeHistory: {
    date: string;
    uptime: number;
    checksTotal: number;
    checksHealthy: number;
  }[];
}

interface StatusData {
  status: "operational" | "degraded" | "outage";
  overallUptime: number;
  services: ServiceStatus[];
  lastUpdated: string;
}

interface StatusProps {
  mode: string;
}

const Status: React.FC<StatusProps> = ({ mode }) => {
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${hostname}/public/status`);
        setStatusData(response.data);
        setError(null);
      } catch (err) {
        setError("Unable to fetch status data");
        // Set default data when API fails
        setStatusData({
          status: "operational",
          overallUptime: 100,
          services: [
            {
              name: "API",
              key: "api",
              description: "Core API services",
              status: "operational",
              uptimePercentage: 100,
              lastChecked: null,
              uptimeHistory: [],
            },
            {
              name: "Database",
              key: "database",
              description: "Data storage",
              status: "operational",
              uptimePercentage: 100,
              lastChecked: null,
              uptimeHistory: [],
            },
            {
              name: "Authentication",
              key: "authentication",
              description: "Login and session management",
              status: "operational",
              uptimePercentage: 100,
              lastChecked: null,
              uptimeHistory: [],
            },
            {
              name: "LLM Provider",
              key: "llm_provider",
              description: "AI language model services",
              status: "operational",
              uptimePercentage: 100,
              lastChecked: null,
              uptimeHistory: [],
            },
          ],
          lastUpdated: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    // Refresh every 60 seconds
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "operational":
        return <CheckCircle sx={{ color: "#22c55e", fontSize: 24 }} />;
      case "degraded":
        return <Warning sx={{ color: "#f59e0b", fontSize: 24 }} />;
      case "outage":
        return <ErrorIcon sx={{ color: "#ef4444", fontSize: 24 }} />;
      default:
        return <CheckCircle sx={{ color: "#22c55e", fontSize: 24 }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "operational":
        return "#22c55e";
      case "degraded":
        return "#f59e0b";
      case "outage":
        return "#ef4444";
      default:
        return "#22c55e";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "operational":
        return "All Systems Operational";
      case "degraded":
        return "Partial System Outage";
      case "outage":
        return "Major System Outage";
      default:
        return "All Systems Operational";
    }
  };

  const getServiceStatusText = (status: string) => {
    switch (status) {
      case "operational":
        return "No issues";
      case "degraded":
        return "Degraded";
      case "outage":
        return "Outage";
      default:
        return "No issues";
    }
  };

  // Generate overall uptime bars combining all services
  const getOverallUptimeBars = () => {
    const bars = [];
    const today = new Date();

    for (let i = 89; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];

      // Find the minimum uptime across all services for this day
      let minUptime = 100;
      if (statusData?.services) {
        for (const service of statusData.services) {
          const dayData = service.uptimeHistory.find((h) => h.date === dateStr);
          if (dayData && dayData.uptime < minUptime) {
            minUptime = dayData.uptime;
          }
        }
      }

      // Determine bar color based on uptime
      let color = "#22c55e"; // green for >= 98%
      if (minUptime < 98 && minUptime >= 95) {
        color = "#f59e0b"; // amber for 95-98%
      } else if (minUptime < 95) {
        color = "#ef4444"; // red for < 95%
      }

      bars.push({
        date: dateStr,
        uptime: minUptime,
        color,
      });
    }

    return bars;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
            }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <CircularProgress size={80} />
            </Box>
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: getStatusColor(statusData?.status || "operational"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircle sx={{ color: "white", fontSize: 48 }} />
            </Box>
          )}

          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 500 }}
          >
            {loading ? (
              <Skeleton width={300} sx={{ mx: "auto" }} />
            ) : (
              getStatusText(statusData?.status || "operational")
            )}
          </Typography>

          {!loading && statusData && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {statusData.overallUptime.toFixed(3)}% uptime over the last 90
              days
            </Typography>
          )}

          {/* Overall Uptime Timeline */}
          {!loading && statusData && (
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: "2px",
                  height: 32,
                  borderRadius: 0.5,
                  overflow: "hidden",
                }}
              >
                {getOverallUptimeBars().map((bar, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" display="block">
                          {formatDate(bar.date)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {bar.uptime.toFixed(2)}% uptime
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: bar.color,
                        minWidth: 3,
                        borderRadius: 0.25,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scaleY(1.2)",
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  90 days ago
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Today
                </Typography>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      {/* Services Status Table */}
      <Container maxWidth="md" sx={{ py: 6, flex: 1 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Current Status by Service
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Skeleton variant="rectangular" height={200} />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableBody>
                {statusData?.services.map((service) => (
                  <TableRow key={service.key}>
                    <TableCell sx={{ width: 40, pr: 0 }}>
                      {getStatusIcon(service.status)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {service.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: getStatusColor(service.status),
                          fontWeight: 500,
                        }}
                      >
                        {getServiceStatusText(service.status)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Recent Incidents */}
        <Typography variant="h5" sx={{ mt: 6, mb: 3 }}>
          Recent Incidents
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <CheckCircle sx={{ color: "#22c55e", fontSize: 40, mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No incidents reported
          </Typography>
        </Paper>

        {/* Legend */}
        <Box sx={{ mt: 4, display: "flex", gap: 3, justifyContent: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: "#22c55e",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              No Issues
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: "#f59e0b",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Degraded
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: "#ef4444",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Outage
            </Typography>
          </Box>
        </Box>

        {/* Last updated */}
        {statusData && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", mt: 4 }}
          >
            Last updated: {new Date(statusData.lastUpdated).toLocaleString()}
          </Typography>
        )}
      </Container>

      <BrandedPageFooter currentPage="status" />
    </Box>
  );
};

export default Status;
